import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Parse the amount input into the number the PATCH body carries, or null
// when it isn't a valid amount. Pure and exported so both the review and
// detail screens share one definition of "valid pay" — and so the rule
// is unit-testable without rendering anything.
//
// Mirrors the server's `money` coercion (packages/pre-settlement/src/app/
// server.ts): finite, >= 0. Blank is invalid here because the driver is
// being asked for a number; the server treats blank as "clear the field",
// which is not what this input means.
export function parseAmount(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  if (n < 0) return null;
  return n;
}

interface AmountFieldProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  showError?: boolean;
  required?: boolean;
}

export function AmountField({ value, onChange, onBlur, showError, required }: AmountFieldProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor="amount" className="text-base">
        Amount{required && <span className="text-destructive"> *</span>}
      </Label>
      <div className="relative">
        <span
          aria-hidden="true"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-lg text-muted-foreground"
        >
          $
        </span>
        <Input
          id="amount"
          // type=number + inputMode=decimal gets the numeric keypad on a
          // phone, which is the only keyboard this screen ever needs.
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          placeholder="0.00"
          aria-label="Amount"
          aria-invalid={showError ? true : undefined}
          className="h-14 pl-8 text-lg tabular-nums"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
        />
      </div>
      {showError && (
        <p role="alert" className="text-xs text-destructive">
          Enter the pay for this load — a number of 0 or more.
        </p>
      )}
    </div>
  );
}
